import type { ComponentChildren } from "preact";
import { IcoCheckCircle, IcoCircle } from "@/components/icons";

type RadioCardProps = {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  children: ComponentChildren;
  /** Compact variant uses smaller padding and icons */
  compact?: boolean;
  /** Additional CSS classes */
  class?: string;
};

/**
 * A styled radio button card with check circle indicator.
 * Use RadioCardContent to structure the label and description.
 */
export function RadioCard({
  name,
  value,
  checked,
  onChange,
  children,
  compact,
  class: className,
}: RadioCardProps) {
  const iconSize = compact ? "size-4" : "size-5";
  const padding = compact ? "px-3 py-2" : "p-3";
  const gap = compact ? "gap-2" : "gap-3";
  // Center icon with the first line of text (label)
  const iconMargin = "mt-1";

  return (
    <label
      class={`flex ${gap} items-start rounded-lg border ${padding} cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-ring-offset ${
        checked
          ? "border-text-subtle bg-surface-selected"
          : "border-border hover:border-text-subtle"
      } ${className ?? ""}`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        class="sr-only"
      />
      {checked ? (
        <IcoCheckCircle
          class={`${iconSize} text-text ${iconMargin} flex-shrink-0`}
        />
      ) : (
        <IcoCircle
          class={`${iconSize} text-text-subtle ${iconMargin} flex-shrink-0`}
        />
      )}
      <span class="flex flex-col gap-0.5 flex-1">{children}</span>
    </label>
  );
}

type RadioCardContentProps = {
  label: string;
  description?: string;
  icon?: ComponentChildren;
};

/**
 * Standard content layout for RadioCard with label, optional icon, and optional description.
 */
export function RadioCardContent({
  label,
  description,
  icon,
}: RadioCardContentProps) {
  return (
    <>
      <span class="flex items-center gap-2">
        {icon && <span class="text-text-muted">{icon}</span>}
        <span class="font-medium text-text">{label}</span>
      </span>
      {description && (
        <span class="text-sm text-text-muted">{description}</span>
      )}
    </>
  );
}
